"use client"

import { Dialog, Transition } from "@headlessui/react"
import Image from "next/image"
import { Fragment, useState } from "react"

import { PSEUDO_ID_COOKIE } from "@lib/constants"
import { clearAuthCookie } from "@lib/util/auth"
import { clearSessionCookie } from "@lib/util/get-session-cookie-client"
import User from "@modules/common/icons/user-1"

interface UserProfileDropdownProps {
  profile: {
    displayName: string
    email: string
  }
  availableCreditFormatted: string
  variant: "contained" | "expanded"
  color: "nelo-black" | "nelo-blue"
}

const UserProfileDropdown = ({ profile, availableCreditFormatted, variant, color }: UserProfileDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  const doLogout = () => {
    clearAuthCookie()
    clearSessionCookie(PSEUDO_ID_COOKIE)
    // forcing a reload so the middleware regenerates the PSEUDO_ID_COOKIE
    window.location.reload()
  }

  return (
    <>
      <button onClick={openModal} className={`text-${color} flex items-center gap-2 rounded-lg px-3 py-2 font-manrope text-sm font-bold`}>
        <User size={20} color={color === "nelo-blue" ? "#067BF2" : "#444444"} />
        {variant === "expanded" && availableCreditFormatted.split(".")[0]}
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-start justify-end p-4 pt-20 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 text-left align-middle font-manrope shadow-xl transition-all">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                        <User size={16} />
                      </div>
                      <div>
                        <h3 className="text-md font-semibold">{profile.displayName}</h3>
                        <p className="text-xs text-nelo-dark-gray">{profile.email}</p>
                      </div>
                    </div>
                    <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  <div className="mb-6 rounded-lg bg-nelo-light-gray p-4 font-manrope">
                    <p className="text-xs text-nelo-dark-gray">Tu crédito Nelo</p>
                    <p className="text-2xl font-bold">{availableCreditFormatted}</p>
                  </div>

                  <button
                    onClick={doLogout}
                    className="flex w-full items-center gap-2 font-manrope text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200"
                  >
                    <Image src="/images/logout.svg" width={24} height={24} alt="Logout" />
                    Cerrar sesión
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default UserProfileDropdown
